import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiData, GiveBadgeAwardBiEvent, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import { ButtonSize } from '@wix/da-ds/pkg/Buttons/types';
import BoostIcon from '@wix/da-ds/pkg/Icons/24x24/Boost';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { pushBoostModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';

export interface Props {
  deviation: PapiDeviation;
  size?: ButtonSize;
  className?: string;
  variant?: 'text' | 'icon' | 'icon-with-background';
}

const BoostButton: FC<Props> = ({ deviation, className, size = 'medium', variant = 'icon' }) => {
  const { t } = useTranslation();
  const { sectionname } = useContext(BiContext);

  // boost is disabled for tiers pending deletion
  const isDisabled = deviation.tier?.state === 'pending_deletion';

  const dispatchPushBoostModal = useAction(pushBoostModal);
  const onClick = useCallback(() => {
    dispatchPushBoostModal(deviation, sectionname);
  }, [deviation, dispatchPushBoostModal, sectionname]);

  const commonProps = {
    className,
    size,
    disabled: isDisabled,
    onClick,
    biData: BiData<GiveBadgeAwardBiEvent>({
      evid: 320,
      sectionname: BiContextProvidedValue,
      itemid: deviation.deviationId,
      typeid: ResourceType.TIER,
      badge_type: 'boost',
      badge_typeid: 176,
    }),
    title: t('studio.table.actions.boost'),
  };

  if (variant === 'text') {
    return (
      <TextButton variant="main" icon={BoostIcon} {...commonProps}>
        {t('studio.table.actions.boost')}
      </TextButton>
    );
  } else {
    const IconComponent = variant === 'icon' ? IconButton : IconButtonWithBackground;
    return (
      <IconComponent
        icon={BoostIcon}
        aria-label={t('studio.table.actions.boost')}
        {...commonProps}
      />
    );
  }
};

BoostButton.displayName = 'BoostButton';
export default BoostButton;
